import React, { useRef, useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { ContextSearch } from '../../Context/SearchContext';
import styles from './LandingPageV4.module.css';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import ScienceIcon from '@mui/icons-material/Science';
import GridViewIcon from '@mui/icons-material/GridView';
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';
import SyncIcon from '@mui/icons-material/Sync';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import SpaIcon from '@mui/icons-material/Spa';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import GrassIcon from '@mui/icons-material/Grass';
import AirIcon from '@mui/icons-material/Air';
import BoltIcon from '@mui/icons-material/Bolt';

const categories = [
  { label: 'Bongs', Icon: ScienceIcon },
  { label: 'Vaporizers', Icon: AirIcon },
  { label: 'Rolling Papers', Icon: StickyNote2Icon },
  { label: 'Dab Rigs', Icon: LocalFireDepartmentIcon },
  { label: 'Herb Grinders', Icon: GrassIcon },
  { label: 'Accessories', Icon: GridViewIcon },
  { label: 'Vapes', Icon: BoltIcon },
  { label: 'Kratom', Icon: EnergySavingsLeafIcon },
  { label: 'Nic Pouches', Icon: Inventory2Icon },
  { label: 'Mushrooms', Icon: PsychologyIcon },
  { label: 'CBD THC', Icon: SpaIcon },
];

const CategoryStrip = ({ onNeedAuth }) => {
  const navigate = useNavigate();
  const { fetchCategoryProducts } = useContext(ContextSearch);
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [fetchingCategory, setFetchingCategory] = useState(null);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) el.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('resize', checkScroll);
    return () => {
      if (el) el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [checkScroll]);

  const scroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollAmount = el.clientWidth * 0.6;
    el.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
  };

  const hasUserLocation = () => {
    const stored = Cookies.get('userLocation');
    if (!stored) return false;
    try {
      const loc = JSON.parse(decodeURIComponent(stored));
      return loc?.lat && loc?.lng;
    } catch {
      return false;
    }
  };

  const handleCategoryClick = async (categoryName) => {
    if (fetchingCategory) return;

    if (!hasUserLocation()) {
      if (onNeedAuth) onNeedAuth(categoryName);
      return;
    }

    try {
      setFetchingCategory(categoryName);
      const responseData = await fetchCategoryProducts(categoryName);
      const smartsearch = responseData.smartsearch || responseData.message?.smartsearch;
      if (smartsearch) {
        navigate(`/category/${smartsearch}?category=${smartsearch}`);
      }
    } catch {} finally {
      setFetchingCategory(null);
    }
  };

  return (
    <section
      id="shop-by-category"
      className={styles.categoryStrip}
      aria-labelledby="category-strip-title"
    >
      <div className={styles.innerWrapper}>
        <div className={styles.categoryHeader}>
          <div className={styles.categoryHeaderText}>
            <p className={styles.sectionEyebrow}>Browse local favorites</p>
            <h2 id="category-strip-title" className={styles.categoryTitle}>
              Find your next favorite.
            </h2>
            <p className={styles.categoryDescription}>
              Explore everyday essentials and new discoveries available from
              trusted shops nearby.
            </p>
          </div>
          <div className={styles.categoryNavButtons}>
            <button
              type="button"
              className={`${styles.categoryArrow} ${!canScrollLeft ? styles.categoryArrowDisabled : ''}`}
              onClick={() => scroll('left')}
              aria-label="Scroll left"
              disabled={!canScrollLeft}
            >
              {/* <span className="material-symbols-outlined">chevron_left</span> */}
              <ChevronLeftIcon sx={{ fontSize: 18 }} aria-hidden />
            </button>
            <button
              type="button"
              className={`${styles.categoryArrow} ${!canScrollRight ? styles.categoryArrowDisabled : ''}`}
              onClick={() => scroll('right')}
              aria-label="Scroll right"
              disabled={!canScrollRight}
            >
              {/* <span className="material-symbols-outlined">chevron_right</span> */}
              <ChevronRightIcon sx={{ fontSize: 18 }} aria-hidden />
            </button>
          </div>
        </div>
        <p className={styles.categorySwipeHint} aria-hidden="true">
          Swipe to explore
          <ChevronRightIcon sx={{ fontSize: 15 }} />
        </p>
        <div className={styles.categoryScrollWrapper}>
          {canScrollLeft && <div className={styles.categoryFadeLeft} />}
          <div className={styles.categoryPills} ref={scrollRef}>
            {categories.map((cat) => {
              const { Icon } = cat;
              return (
                <button
                  type="button"
                  key={cat.label}
                  className={styles.categoryPill}
                  onClick={() => handleCategoryClick(cat.label)}
                  disabled={Boolean(fetchingCategory)}
                  aria-busy={fetchingCategory === cat.label}
                  aria-label={
                    fetchingCategory === cat.label
                      ? `Loading ${cat.label}`
                      : `Browse ${cat.label}`
                  }
                  style={{
                    opacity: fetchingCategory && fetchingCategory !== cat.label ? 0.5 : 1,
                  }}
                >
                  <div className={styles.categoryIconContainer}>
                    {fetchingCategory === cat.label ? (
                      <SyncIcon
                        className={styles.categoryIconLoader}
                        sx={{ fontSize: 32 }}
                        aria-hidden
                      />
                    ) : (
                      <Icon sx={{ fontSize: 32 }} aria-hidden />
                    )}
                  </div>
                  <span className={styles.categoryPillLabel}>
                    {fetchingCategory === cat.label ? 'Loading...' : cat.label}
                  </span>
                  <NorthEastIcon
                    className={styles.categoryPillArrow}
                    sx={{ fontSize: 15 }}
                    aria-hidden="true"
                  />
                </button>
              );
            })}
          </div>
          {canScrollRight && <div className={styles.categoryFadeRight} />}
        </div>
      </div>
    </section>
  );
};

export default CategoryStrip;
